import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

export interface Props {
    currentValue?: number,
    previousValue?: number,
    percentage: boolean,
    title: string;
    previousTitle: string;
    unit?: string;
    suffix?: string;
    titleTooltip?: string;
    onClick?: () => void;
}

const colorGreen = 'rgb(var(--color-success))';
const colorRed = 'rgb(var(--color-danger))';

export interface Growth {
    display: string;
    color: string;
    up: boolean;
}

/** Period-over-period change, or null when there is no meaningful comparison. */
export function computeGrowth(currentValue?: number, previousValue?: number, percentage = true): Growth | null {
    if (currentValue == null || previousValue == null) return null;
    if (previousValue < 1 || currentValue < 1) return null;

    const growth = ((currentValue - previousValue) / previousValue) * 100.0;

    if (growth > 100) {
        return { display: (currentValue / previousValue).toFixed(1) + 'x', color: colorGreen, up: true };
    }
    if (growth > 0) {
        return { display: growth.toFixed(2) + (percentage ? '%' : ''), color: colorGreen, up: true };
    }
    if (growth < 0) {
        return { display: growth.toFixed(2) + (percentage ? '%' : ''), color: colorRed, up: false };
    }
    return null;
}

/** Compact inline delta for dense rows, using the same math and colors as GrowthStatistic. */
export function GrowthDelta({ currentValue, previousValue }: { currentValue?: number; previousValue?: number }) {
    const growth = computeGrowth(currentValue, previousValue);
    if (!growth) {
        return <span className="text-text-3 font-mono text-2xs tabular-nums">—</span>;
    }
    return (
        <span className="font-mono text-2xs tabular-nums whitespace-nowrap" style={{ color: growth.color }}>
            {growth.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {growth.display}
        </span>
    );
}

const GrowthStatistic = (props: Props) => {
    const titleElement = props.titleTooltip ? (
        <Tooltip title={props.titleTooltip}><span>{props.title}</span></Tooltip>
    ) : <span>{props.title}</span>;

    const hasValue = props.currentValue !== undefined && props.currentValue !== null;

    const previousTooltip = props.previousTitle
        ? props.previousTitle + ': ' + (props.previousValue ? (props.unit || '') : '') + (props.previousValue ? Math.round(props.previousValue).toLocaleString() : 'N/A')
        : undefined;

    if (!hasValue) {
        return (
            <div className="min-w-[100px]">
                <div className="text-2xs text-text-3 uppercase tracking-wider font-medium mb-1 leading-tight">{titleElement}</div>
                <div className="text-base text-text-3 font-mono tabular-nums">
                    {previousTooltip ? <Tooltip title={previousTooltip}><span>—</span></Tooltip> : '—'}
                </div>
            </div>
        );
    }

    const currentValue = props.currentValue!;
    const growth = computeGrowth(currentValue, props.previousValue, props.percentage);

    let growthDisplay = growth?.display ?? '';
    const color = growth?.color ?? '';
    const prefix: React.ReactNode = growth ? (growth.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />) : null;

    if (!growth && props.previousValue != null && !(props.previousValue >= 1 && currentValue >= 1)) {
        growthDisplay = '-';
    }

    const isClickable = props.onClick && hasValue;
    const valueFormatted = (props.unit || '') + Math.round(currentValue).toLocaleString() + (props.suffix || '');

    const valueNode = (
        <span className={`text-md font-semibold font-mono tabular-nums leading-none ${isClickable ? 'text-accent' : 'text-text'}`}>
            {valueFormatted}
        </span>
    );

    return (
        <div onClick={props.onClick} className={`min-w-[100px] ${isClickable ? 'cursor-pointer' : ''}`}>
            <div className="text-2xs text-text-3 uppercase tracking-wider font-medium mb-1 leading-tight">{titleElement}</div>
            <div>
                {previousTooltip ? <Tooltip title={previousTooltip}>{valueNode}</Tooltip> : valueNode}
            </div>
            {growthDisplay && (
                <div className="text-xs mt-0.5 font-mono tabular-nums" style={{ color }}>
                    {prefix} {growthDisplay}
                </div>
            )}
        </div>
    );
};
export default GrowthStatistic;
